import { useTranslate, CanAccess, useGo } from "@refinedev/core";
import {
  Flex,
  Popover,
  Button,
  Typography,
  Avatar,
  Badge,
  Divider,
} from "antd";
import { useState, type CSSProperties } from "react";
import type { GlobalToken } from "antd";
import styled from "styled-components";
import { User } from "types";
import { useAntTheme } from "hooks/useAntTheme";
import {
  CaretDownIcon,
  LightningIcon,
  MegaphoneIcon,
  UserIcon,
  UserPlusIcon,
  BuildingsIcon,
  UsersThreeIcon,
  GraduationCapIcon,
  CookieIcon,
} from "@phosphor-icons/react";
import { radius, sidebarTokens, typography } from "@libertify/ui";
import {
  NAV_TEXT_COLOR,
  sidebarMenuItemStyle,
} from "@/components/layout/sider/styles";
import { useUnreadWhatsNew } from "pages/whats-new/hooks/useUnreadWhatsNew";
import { getInitials } from "@/utils/text";
import { useCookieSettings } from "@/providers/CookieConsentProvider";

const MENU_ITEM_ICON_SIZE = 24;

// Design spec for the popover container: 18px top, 12px (token paddingSM)
// on the other sides.
const MENU_PADDING_TOP = 18;
// Design spec for the email / plan lines in the popover header.
const MENU_DETAIL_LINE_HEIGHT = 1.5;

const dividerStyle: CSSProperties = {
  margin: "10px 0px",
  borderColor: sidebarTokens.dividerColor,
  borderWidth: 1,
};

export const UserMenu = ({
  collapsed = false,
  user,
  logout,
  isMobile,
  toggleSidebar,
  planName,
  isCustom = false,
}: {
  collapsed: boolean;
  user: User | undefined;
  logout: false | JSX.Element | undefined;
  isMobile: boolean;
  toggleSidebar: (collapsed: boolean) => void;
  planName?: string;
  isCustom?: boolean;
}) => {
  const t = useTranslate();
  const go = useGo();
  const { theme } = useAntTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const displayPlan = !isCustom && planName;
  const { hasUnread: hasUnreadWhatsNew } = useUnreadWhatsNew();
  const { openCookieSettings } = useCookieSettings();

  const handleUserMenuClick = (open: boolean, link: string) => {
    go({
      to: link,
    });

    setShowUserMenu(open);
    if (isMobile) {
      toggleSidebar(true);
    }
  };

  const handleCookieSettingsClick = () => {
    setShowUserMenu(false);
    if (isMobile) {
      toggleSidebar(true);
    }
    openCookieSettings();
  };

  const name = getInitials(user?.first_name, user?.last_name);

  if (isMobile && collapsed) return; //collapsed width for sidebar when isMobile === true is 0
  return (
    <Popover
      // The menu is wider than the sidebar, so a right-aligned popover got
      // pushed flush against the screen edge. Anchor it left, 16px in, keeping
      // the default 4px gap above the trigger.
      placement="topLeft"
      align={{ offset: [theme.padding, -theme.marginXXS] }}
      arrow={false}
      trigger="click"
      open={showUserMenu}
      onOpenChange={() => setShowUserMenu((prev) => !prev)}
      style={{ zIndex: 9999 }}
      styles={{
        body: {
          padding: `${MENU_PADDING_TOP}px ${theme.paddingSM}px ${theme.paddingSM}px`,
        },
      }}
      content={
        <Flex vertical align="start">
          <StyledMenuHeader $token={theme}>
            <StyledMenuUserName>
              {user?.first_name} {user?.last_name}
            </StyledMenuUserName>
            <StyledMenuDetail $token={theme}>{user?.email}</StyledMenuDetail>
            {displayPlan && (
              <StyledMenuDetail $token={theme}>
                {t("src.components.UserMenu.currentPlan")}: {planName}
              </StyledMenuDetail>
            )}
          </StyledMenuHeader>
          <Button
            block
            type="text"
            style={sidebarMenuItemStyle}
            icon={
              <span className="anticon">
                <BuildingsIcon size={MENU_ITEM_ICON_SIZE} />
              </span>
            }
            onClick={() => handleUserMenuClick(false, "/organization")}
          >
            {t("src.components.UserMenu.organizationSettings")}
          </Button>

          <Button
            block
            type="text"
            style={sidebarMenuItemStyle}
            icon={<UserIcon size={MENU_ITEM_ICON_SIZE} />}
            onClick={() => handleUserMenuClick(false, "/settings")}
          >
            {t("src.components.UserMenu.profileDetails")}
          </Button>
          <Button
            block
            type="text"
            style={sidebarMenuItemStyle}
            icon={<GraduationCapIcon size={MENU_ITEM_ICON_SIZE} />}
            onClick={() => handleUserMenuClick(false, "/tutorials")}
          >
            {t("src.components.UserMenu.tutorials")}
          </Button>
          <CanAccess resource="subscriptions" action="list">
            <Button
              block
              type="text"
              style={sidebarMenuItemStyle}
              icon={<LightningIcon size={MENU_ITEM_ICON_SIZE} />}
              onClick={() => handleUserMenuClick(false, "/subscription")}
            >
              {t("pages.subscription.title")}
            </Button>
          </CanAccess>
          <CanAccess resource="subscriptions_users" action="list">
            <Button
              block
              type="text"
              style={sidebarMenuItemStyle}
              icon={
                <span className="anticon">
                  <UsersThreeIcon size={MENU_ITEM_ICON_SIZE} />
                </span>
              }
              onClick={() =>
                handleUserMenuClick(false, "/subscription/members")
              }
            >
              {t("src.components.UserMenu.subscriptionUsers")}
            </Button>
          </CanAccess>
          <CanAccess resource="user_invite" action="list">
            <Button
              block
              type="text"
              style={sidebarMenuItemStyle}
              icon={<UserPlusIcon size={MENU_ITEM_ICON_SIZE} />}
              onClick={() => handleUserMenuClick(false, "/invitations")}
            >
              {t("src.App.invitations")}
            </Button>
          </CanAccess>
          <Button
            block
            type="text"
            style={sidebarMenuItemStyle}
            icon={
              <span className="anticon">
                <Badge dot={hasUnreadWhatsNew} offset={[-2, 2]}>
                  <MegaphoneIcon
                    size={MENU_ITEM_ICON_SIZE}
                    color={NAV_TEXT_COLOR}
                  />
                </Badge>
              </span>
            }
            onClick={() => handleUserMenuClick(false, "/whats-new")}
          >
            {t("src.components.UserMenu.whatsNew")}
          </Button>
          <Button
            block
            type="text"
            style={sidebarMenuItemStyle}
            icon={<CookieIcon size={MENU_ITEM_ICON_SIZE} />}
            onClick={handleCookieSettingsClick}
          >
            {t("cookieDisclaimer.settings")}
          </Button>
          <Divider plain style={dividerStyle} />
          {logout}
        </Flex>
      }
    >
      <StyledUserMenuTrigger
        type="button"
        aria-expanded={showUserMenu}
        $token={theme}
        $collapsed={collapsed}
      >
        <Flex align="center" gap={theme.paddingXS} style={{ minWidth: 0 }}>
          <Avatar
            style={{
              backgroundColor: theme.colorPrimary,
              color: "white",
              minWidth: 48,
              minHeight: 48,
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {name}
          </Avatar>
          {!collapsed && (
            <Flex vertical style={{ minWidth: 0 }}>
              <StyledUserName ellipsis>
                {user?.first_name + " " + user?.last_name}
              </StyledUserName>
              {displayPlan && (
                <StyledPlanName $token={theme}>{planName}</StyledPlanName>
              )}
            </Flex>
          )}
        </Flex>
        {!collapsed && (
          <CaretDownIcon
            weight="bold"
            size={20}
            style={{
              flexShrink: 0,
              color: theme.colorTextSecondary,
              opacity: 0.5,
            }}
          />
        )}
      </StyledUserMenuTrigger>
    </Popover>
  );
};

// ─── Styled components ───

/*
 * Popover header (name, email, plan): the same tinted block + line as the
 * sidebar's muted "Custom features" section. It bleeds to the popover's
 * top/side edges; padding 20px (paddingMD) 36px (container paddingSM + item
 * paddingLG, so the text lines up with the item icons) 16px (padding).
 */
const StyledMenuHeader = styled.div<{ $token: GlobalToken }>`
  ${({ $token }) => `
    margin: -${MENU_PADDING_TOP}px -${$token.paddingSM}px ${$token.paddingSM}px;
    padding: ${$token.paddingMD}px ${$token.paddingSM + $token.paddingLG}px
      ${$token.padding}px;
    border-radius: ${radius.box}px ${radius.box}px 0 0;
    border-bottom: ${$token.lineWidth}px solid ${sidebarTokens.dividerColor};
  `}
  align-self: stretch;
  display: flex;
  flex-direction: column;
  background: ${sidebarTokens.mutedSectionBg};
`;

const StyledMenuUserName = styled(Typography.Text)`
  &.ant-typography {
    font-family: ${typography.fontFamily.primary};
    font-size: ${typography.body.large.fontSize}px;
    font-weight: ${typography.fontWeight.medium};
    color: ${NAV_TEXT_COLOR};
    text-transform: capitalize;
  }
`;

const StyledMenuDetail = styled(Typography.Text)<{ $token: GlobalToken }>`
  &.ant-typography {
    font-family: ${sidebarTokens.itemFont.fontFamily};
    font-size: ${sidebarTokens.itemFont.fontSize}px;
    letter-spacing: ${sidebarTokens.itemFont.letterSpacing};
    font-weight: ${typography.fontWeight.regular};
    line-height: ${MENU_DETAIL_LINE_HEIGHT};
    color: ${({ $token }) => $token.colorTextSecondary};
  }
`;

/*
 * Full-width footer row: it bleeds out of the lib Sidebar footer's row inset
 * (token paddingLG when expanded, none when collapsed) so its hover background
 * spans the sidebar like the nav items, and owns the footer's vertical spacing.
 */
const StyledUserMenuTrigger = styled.button<{
  $token: GlobalToken;
  $collapsed: boolean;
}>`
  ${({ $token, $collapsed }) => {
    const bleed = $collapsed ? 0 : $token.paddingLG;
    return `
      width: calc(100% + ${bleed * 2}px);
      margin: 0 -${bleed}px;
      padding: ${$token.padding}px ${bleed}px;
      justify-content: ${$collapsed ? "center" : "space-between"};
    `;
  }}
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.paddingXS}px;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background ${({ $token }) => $token.motionDurationFast}
    ease-in-out;

  &:hover {
    background: ${sidebarTokens.itemHoverBg};
  }

  &:focus-visible {
    outline: ${({ $token }) =>
      `${$token.lineWidthBold}px solid ${$token.colorPrimary}`};
    outline-offset: -${({ $token }) => $token.lineWidthBold}px;
  }
`;

const StyledUserName = styled(Typography.Text)`
  &.ant-typography {
    font-family: ${sidebarTokens.itemFont.fontFamily};
    font-size: ${sidebarTokens.itemFont.fontSize}px;
    letter-spacing: ${sidebarTokens.itemFont.letterSpacing};
    color: ${NAV_TEXT_COLOR};
    text-transform: capitalize;
  }
`;

// Plan names come from the product name; only their first letter is forced
// to uppercase (unlike `capitalize`, which would touch every word).
const StyledPlanName = styled(Typography.Text)<{ $token: GlobalToken }>`
  &.ant-typography {
    display: block;
    font-family: ${sidebarTokens.itemFont.fontFamily};
    font-size: ${({ $token }) => $token.fontSizeSM}px;
    letter-spacing: ${sidebarTokens.itemFont.letterSpacing};
    color: ${sidebarTokens.subtleTextColor};
  }

  &::first-letter {
    text-transform: uppercase;
  }
`;
